import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './diary-classroom-list.module.css';

// Components
import ErrorComponent from '../../components/error/error';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';
import AbsenceActionsModal from '../../components/absence-actions-modal/absence-actions-modal';
import ClassroomDay from '../../components/classroom-day/classroom-day';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import DiaryService from '../../services/diary-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

// Helpers
import { getTodayDateString } from '../../helpers/absence-helper';
import { classroomDayOf, classroomDayQuery } from '../../helpers/daily-records-helper';

const DiaryClassroomList = () => {
  const { classroomUuid } = useParams<{ classroomUuid: string }>();
  const navigate = useNavigate();
  const { setModalContent } = useContext(GeneralContext);
  const [error, setError] = useState(false);
  const [schoolUuid, setSchoolUuid] = useState<string>(null);

  const today = getTodayDateString();
  const [searchParams] = useSearchParams();
  const day = classroomDayOf(searchParams.get('dia'), today);
  const dayQuery = classroomDayQuery(day, today);

  useEffect(() => {
    new SchoolService()
      .getSchoolById(new StorageService().getEmployee().schoolId)
      .then((school) => setSchoolUuid(school.uuid))
      .catch(() => setError(true));
  }, []);

  const [classroomDay, setClassroomDay] = useState<ClassroomDiaryDay>(null);
  useEffect(() => {
    if (!schoolUuid) return;

    let isCurrent = true;
    new DiaryService(schoolUuid)
      .getClassroomDay(classroomUuid, day)
      .then((newDay) => isCurrent && setClassroomDay(newDay))
      .catch(() => isCurrent && setError(true));

    return () => {
      isCurrent = false;
    };
  }, [schoolUuid, classroomUuid, day]);

  const openAbsenceActionsModal = () => {
    setModalContent({
      title: 'Gerenciar faltas',
      modalContent: <AbsenceActionsModal classroomUuid={classroomUuid} />,
      submitButtonTitle: '',
      submitButtonType: 'terciary',
      submitData: {},
      onSubmitButtonClick: Promise.resolve(),
      isOpen: true,
    });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomDay) return <Loading type='contentFull' />;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h2>diários do {classroomDay.classroomName || ''}</h2>
        <label className={styles.dayField}>
          <span className={styles.srOnly}>Dia</span>
          <input
            type='date'
            value={day}
            max={today}
            onChange={(e) =>
              e.target.value && navigate(`/diarios/turma/${classroomUuid}${classroomDayQuery(e.target.value, today)}`)
            }
          />
        </label>
        <span className={styles.spacer} />
        <Link to={`/diarios/turma/${classroomUuid}/historico${dayQuery}`}>
          <SubmitButton value='histórico' variant='secondary' />
        </Link>
        <SubmitButton value='faltas' variant='secondary' onClick={openAbsenceActionsModal} />
      </div>
      <div className={`${styles.pageBody} shadow-box`}>
        <h3 className={styles.sectionTitle}>Dia da turma</h3>
        <ClassroomDay
          day={day}
          today={today}
          classroomDay={classroomDay}
          fillPath={`/diarios/turma/${classroomUuid}/preencher`}
          groupPath={(group) => `/diarios/turma/${classroomUuid}/grupo/${group.id}${dayQuery}`}
        />
      </div>
    </div>
  );
};

export default DiaryClassroomList;
